import QRCode from 'qrcode';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import OtpInput from 'react-otp-input';
import { useHistory, useLocation } from 'react-router-dom';

import ErrorMessage from '@/components/atoms/ErrorMessage';
import { Button } from '@/components/atoms/Button';
import { Routes } from '@/core/router/routes.types';
import { useAuthSuccess } from '@/hooks/useAuthSuccess';
import logo from '@/pages/Login/logo.svg';
import {
    useConfirmTotpMutation,
    useLazyMeQuery,
    useSetupTotpMutation,
} from '@/services/innovorder/auth/auth.endpoints';
import { getErrorCode, stringifyError } from '@/utils/errors';

import {
    Actions,
    BackupCodeItem,
    BackupCodesList,
    Card,
    CodeInputWrapper,
    CopySecretButton,
    Header,
    Logo,
    OtpBox,
    Page,
    ProductTitle,
    QrCodeWrapper,
    Subtitle,
    TabButton,
    Tabs,
    Title,
} from './Mfa.style';
import WebAuthnTab from './MfaSetup/WebAuthnTab';

type ActiveTab = 'totp' | 'webauthn';

const TOTP_LENGTH = 6;
const OTP_CONTAINER_STYLE: React.CSSProperties = {
    display: 'flex',
    gap: '12px',
    justifyContent: 'center',
};

type LocationState = { mfaToken?: string };

const MfaSetup: React.FunctionComponent = () => {
    const history = useHistory();
    const location = useLocation<LocationState>();
    const mfaToken = location.state?.mfaToken ?? null;

    // Detect WebAuthn browser support once. SSR-safe: `window` is undefined during prerender.
    const webauthnSupported = useMemo(
        () => typeof window !== 'undefined' && typeof window.PublicKeyCredential !== 'undefined',
        [],
    );
    const [activeTab, setActiveTab] = useState<ActiveTab>('totp');

    const [setupTotp, { data: setupData, error: setupError, isLoading: setupLoading }] = useSetupTotpMutation();
    const [confirmTotp, { data: confirmData, error: confirmError, isLoading: confirmLoading }] =
        useConfirmTotpMutation();
    const [getMe, { data: meData, error: meError }] = useLazyMeQuery();
    const { handleAuthSuccess, error: authSuccessError } = useAuthSuccess();

    const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
    const [code, setCode] = useState('');
    const [bearerToken, setBearerToken] = useState<string | null>(null);
    const [backupCodesAcknowledged, setBackupCodesAcknowledged] = useState(false);
    const [secretCopied, setSecretCopied] = useState(false);

    // The QR encodes an `otpauth://totp/Issuer:email?secret=BASE32&…` URI. Authenticator apps
    // accept the base32 secret directly when manual entry is needed (no camera, VM, etc.).
    const totpSecret = useMemo(() => {
        if (!setupData?.qrCodeUri) return null;
        try {
            return new URL(setupData.qrCodeUri).searchParams.get('secret');
        } catch {
            return null;
        }
    }, [setupData?.qrCodeUri]);

    const handleCopySecret = useCallback(() => {
        if (!totpSecret) return;
        navigator.clipboard.writeText(totpSecret).then(() => {
            setSecretCopied(true);
            window.setTimeout(() => setSecretCopied(false), 1500);
        });
    }, [totpSecret]);

    // Fire setupTotp at most once per token. Re-firing it (on tab toggle, StrictMode double-mount,
    // etc.) would overwrite the Redis-stored secret server-side, so a QR scanned earlier would no
    // longer match — confirm would then return "Invalid TOTP code" for a code derived from the
    // stale QR.
    const setupFiredRef = useRef(false);
    useEffect(() => {
        if (!mfaToken) {
            history.replace(Routes.Login);
            return;
        }
        if (activeTab === 'totp' && !setupFiredRef.current) {
            setupFiredRef.current = true;
            setupTotp({ token: mfaToken });
        }
    }, [mfaToken, history, setupTotp, activeTab]);

    useEffect(() => {
        if (!setupData?.qrCodeUri) return;
        let cancelled = false;
        QRCode.toDataURL(setupData.qrCodeUri, { width: 200, margin: 1 }).then((dataUrl) => {
            if (!cancelled) setQrDataUrl(dataUrl);
        });
        return () => {
            cancelled = true;
        };
    }, [setupData]);

    const handleConfirm = useCallback(
        (e: React.FormEvent) => {
            e.preventDefault();
            if (!mfaToken || !code) return;
            confirmTotp({ token: mfaToken, code });
        },
        [mfaToken, code, confirmTotp],
    );

    // Auto-confirm when the 6th digit lands so the user doesn't have to click Confirm.
    const handleOtpChange = useCallback(
        (next: string): void => {
            setCode(next);
            if (next.length === TOTP_LENGTH && mfaToken && !confirmLoading && qrDataUrl) {
                confirmTotp({ token: mfaToken, code: next });
            }
        },
        [mfaToken, confirmTotp, confirmLoading, qrDataUrl],
    );

    const handleFinish = useCallback(() => {
        if (!confirmData?.accessToken) return;
        // 3rd-party redirect flow — same contract as MfaVerify: when the API echoes a
        // validated redirectUri, hand the bearer back to the originating app.
        if (confirmData.redirectUri) {
            const separator = confirmData.redirectUri.includes('?') ? '&' : '?';
            window.location.assign(`${confirmData.redirectUri}${separator}bearer=${confirmData.accessToken}`);
            return;
        }
        setBearerToken(confirmData.accessToken);
        getMe({ token: confirmData.accessToken });
        setBackupCodesAcknowledged(true);
    }, [confirmData, getMe]);

    useEffect(() => {
        if (backupCodesAcknowledged && meData && bearerToken) {
            handleAuthSuccess(meData, bearerToken);
        }
    }, [backupCodesAcknowledged, meData, bearerToken, handleAuthSuccess]);

    const confirmErrorCode = confirmError ? getErrorCode(confirmError) : null;
    const errorLabel =
        confirmErrorCode === 'mfa_verification_failed' ? (
            <FormattedMessage id="mfa.setup.invalidCode" />
        ) : confirmErrorCode === 'mfa_token_expired' || confirmErrorCode === 'mfa_token_required' ? (
            <FormattedMessage id="mfa.verify.tokenExpired" />
        ) : authSuccessError === 'no_brands_configured' ? (
            <FormattedMessage id="login.noBrandsConfigured" />
        ) : authSuccessError === 'guest_brand_login_failed' ? (
            <FormattedMessage id="login.guestBrandLoginFailed" />
        ) : (
            stringifyError(setupError) || stringifyError(confirmError) || stringifyError(meError)
        );

    if (confirmData && !backupCodesAcknowledged) {
        return (
            <Page>
                <Header>
                    <Logo src={logo} alt="Innovorder" />
                    <ProductTitle>Backoffice</ProductTitle>
                </Header>
                <Card onSubmit={(e) => e.preventDefault()}>
                    <Title>
                        <FormattedMessage id="mfa.setup.backupTitle" />
                    </Title>
                    <Subtitle>
                        <FormattedMessage id="mfa.setup.backupSubtitle" />
                    </Subtitle>
                    <BackupCodesList>
                        {confirmData.backupCodes.map((backupCode) => (
                            <BackupCodeItem key={backupCode}>{backupCode}</BackupCodeItem>
                        ))}
                    </BackupCodesList>
                    <Actions>
                        <Button buttonType="primary" fullWidth thick onClick={handleFinish}>
                            <FormattedMessage id="mfa.setup.backupAcknowledge" />
                        </Button>
                    </Actions>
                </Card>
            </Page>
        );
    }

    return (
        <Page>
            <Header>
                <Logo src={logo} alt="Innovorder" />
                <ProductTitle>Backoffice</ProductTitle>
            </Header>
            <Card onSubmit={activeTab === 'totp' ? handleConfirm : (e): void => e.preventDefault()}>
                <Tabs>
                    <TabButton type="button" active={activeTab === 'totp'} onClick={(): void => setActiveTab('totp')}>
                        <FormattedMessage id="mfa.setup.tabs.totp" />
                    </TabButton>
                    <TabButton
                        type="button"
                        active={activeTab === 'webauthn'}
                        disabled={!webauthnSupported}
                        onClick={(): void => {
                            if (webauthnSupported) setActiveTab('webauthn');
                        }}
                    >
                        <FormattedMessage id="mfa.setup.tabs.webauthn" />
                    </TabButton>
                </Tabs>

                {activeTab === 'totp' ? (
                    <>
                        <Title>
                            <FormattedMessage id="mfa.setup.title" />
                        </Title>
                        <Subtitle>
                            <FormattedMessage id="mfa.setup.subtitle" />
                        </Subtitle>
                        <QrCodeWrapper>
                            {setupLoading && !qrDataUrl ? (
                                <FormattedMessage id="mfa.setup.loadingQr" />
                            ) : qrDataUrl ? (
                                <img src={qrDataUrl} alt="TOTP QR code" />
                            ) : null}
                        </QrCodeWrapper>
                        {totpSecret ? (
                            <CopySecretButton type="button" onClick={handleCopySecret}>
                                <FormattedMessage id={secretCopied ? 'mfa.setup.copied' : 'mfa.setup.copySecret'} />
                            </CopySecretButton>
                        ) : null}
                        <CodeInputWrapper>
                            <OtpInput
                                value={code}
                                onChange={handleOtpChange}
                                numInputs={TOTP_LENGTH}
                                inputType="tel"
                                skipDefaultStyles
                                containerStyle={OTP_CONTAINER_STYLE}
                                renderInput={(props): React.ReactElement => <OtpBox {...props} />}
                            />
                        </CodeInputWrapper>

                        {errorLabel ? <ErrorMessage>{errorLabel}</ErrorMessage> : null}

                        <Actions>
                            <Button
                                buttonType="primary"
                                fullWidth
                                thick
                                disabled={code.length !== TOTP_LENGTH || confirmLoading || !qrDataUrl}
                                onClick={handleConfirm}
                            >
                                <FormattedMessage id="mfa.setup.confirm" />
                            </Button>
                        </Actions>
                    </>
                ) : (
                    mfaToken && <WebAuthnTab mfaToken={mfaToken} />
                )}
            </Card>
        </Page>
    );
};

export default MfaSetup;
